'use client';

import { useEffect } from 'react';
import Link from 'next/link';
import { Button } from '@/components/ui/button';

/**
 * Error boundary for ALL /blog routes. The blog pages are `force-dynamic`, so a
 * failed fetch/render previously fell through to the generic unbranded Next.js
 * error page. This shows a branded message + retry inside the blog shell, so a
 * transient API failure on the SEO/GEO content never surfaces as a raw crash.
 */
export default function BlogError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.error('[blog] render error:', error);
  }, [error]);

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-10">
      <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 text-center">
        <h1 className="text-2xl font-semibold text-foreground">Something went wrong</h1>
        <p className="max-w-md text-sm text-muted-foreground">
          We couldn&rsquo;t load this page right now. Please try again in a moment.
        </p>
        <div className="mt-2 flex items-center gap-4">
          <Button onClick={() => reset()}>Try again</Button>
          <Link href="/blog" className="text-sm text-primary hover:underline">
            Back to blog
          </Link>
        </div>
      </div>
    </div>
  );
}
